import { useEffect, useState } from 'react';

interface SelectionCountdownTimerProps {
  targetTime: Date;
  serverTimeNow: () => Date;
  heading: string;
  subheading: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

function calculateTimeRemaining(targetTime: Date, now: Date): TimeRemaining {
  const total = targetTime.getTime() - now.getTime();

  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds, total };
}

export function SelectionCountdownTimer({
  targetTime,
  serverTimeNow,
  heading,
  subheading,
}: SelectionCountdownTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    calculateTimeRemaining(targetTime, serverTimeNow()),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(targetTime, serverTimeNow()));
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTime, serverTimeNow]);

  const formatNumber = (num: number) => String(num).padStart(2, '0');

  return (
    <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
      <div className="space-y-6">
        <div className="border-primary/20 border-b pb-4">
          <h2 className="font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
            {heading}
          </h2>
        </div>
        <div className="border-primary border-l-4 pl-4">
          <p className="text-primary/70 text-sm sm:text-base">{subheading}</p>
        </div>
        <div className="flex flex-wrap items-start justify-center gap-2 pt-4 sm:gap-4">
          {timeRemaining.days > 0 && (
            <div className="flex flex-col items-center">
              <div className="rounded-lg bg-primary px-3 py-2 font-bold font-mono text-2xl text-primary-foreground sm:px-6 sm:py-4 sm:text-4xl">
                {formatNumber(timeRemaining.days)}
              </div>
              <span className="mt-1 text-muted-foreground text-xs sm:mt-2 sm:text-sm">
                {timeRemaining.days === 1 ? 'día' : 'días'}
              </span>
            </div>
          )}

          <div className="flex flex-col items-center">
            <div className="rounded-lg bg-primary px-3 py-2 font-bold font-mono text-2xl text-primary-foreground sm:px-6 sm:py-4 sm:text-4xl">
              {formatNumber(timeRemaining.hours)}
            </div>
            <span className="mt-1 text-muted-foreground text-xs sm:mt-2 sm:text-sm">
              horas
            </span>
          </div>

          <div className="flex h-10 items-center font-bold text-2xl sm:h-16 sm:text-4xl">
            :
          </div>

          <div className="flex flex-col items-center">
            <div className="rounded-lg bg-primary px-3 py-2 font-bold font-mono text-2xl text-primary-foreground sm:px-6 sm:py-4 sm:text-4xl">
              {formatNumber(timeRemaining.minutes)}
            </div>
            <span className="mt-1 text-muted-foreground text-xs sm:mt-2 sm:text-sm">
              minutos
            </span>
          </div>

          <div className="flex h-10 items-center font-bold text-2xl sm:h-16 sm:text-4xl">
            :
          </div>

          <div className="flex flex-col items-center">
            <div className="rounded-lg bg-primary px-3 py-2 font-bold font-mono text-2xl text-primary-foreground sm:px-6 sm:py-4 sm:text-4xl">
              {formatNumber(timeRemaining.seconds)}
            </div>
            <span className="mt-1 text-muted-foreground text-xs sm:mt-2 sm:text-sm">
              segundos
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
